/* Importing the useCallback, useState, and useEffect hooks. */
import { useCallback, useState, useEffect } from 'react';

/* Importing the PropTypes module from the prop-types package. */
import PropTypes from 'prop-types';

/* Importing the useSelector and useDispatch hooks from the react-redux library. */
import { useSelector, useDispatch } from 'react-redux';

/* Importing from the react-router-dom package. */
import { useSearchParams } from 'react-router-dom';

import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers';

/* Importing the components from the Material UI library. */
import { Grid, LinearProgress, MenuItem, Stack, TextField } from '@mui/material';

import { ResponsiveLine } from '@nivo/line';

import { searchObservation } from '../../features/observation/observationSlice';

import {
  BloodDropIcon,
  BloodPressureIcon,
  CoronaryCareUnitIcon,
  HeartCardiogramIcon,
  RespirologyIcon,
  ThermometerIcon,
} from '../../components/HealthIcons';

PatientSectionMonitoringCharts.propTypes = {
  patientID: PropTypes.string,
  pagePatient: PropTypes.object,
};

const ObservationTypes = [
  {
    type: 'FHR',
    icon: HeartCardiogramIcon,
    units: 'BPM',
    color: '#00FF00',
    code: '55283-6',
    upperLimit: 'heartrate_upper_bpm',
    lowerLimit: 'heartrate_lower_bpm',
    display: true,
  },
  {
    type: 'TOCO',
    icon: HeartCardiogramIcon,
    units: 'mmHG',
    color: '#00FF00',
    code: '76282-3',
    upperLimit: 'toco_upper_contraction',
    lowerLimit: 'toco_lower_contraction',
    display: true,
  },
  {
    type: 'HR',
    icon: HeartCardiogramIcon,
    units: 'BPM',
    color: '#00FF00',
    code: '76282-3',
    upperLimit: 'heartrate_upper_bpm',
    lowerLimit: 'heartrate_lower_bpm',
    display: true,
  },
  {
    type: 'SPO2',
    icon: BloodDropIcon,
    units: '%',
    color: '#00FFFF',
    code: '59407-7',
    upperLimit: 'oxygen_upper_saturation',
    lowerLimit: 'oxygen_lower_saturation',
    display: true,
  },
  {
    type: 'RESP',
    icon: RespirologyIcon,
    units: 'RPM',
    color: '#FFFF00',
    code: '76270-8',
    upperLimit: 'respiratory_upper_rpm',
    lowerLimit: 'respiratory_lower_rpm',
    display: true,
  },
  {
    type: 'PR',
    icon: CoronaryCareUnitIcon,
    units: 'BPM',
    color: '#00FFFF',
    code: '8889-8',
    upperLimit: 'pulserate_upper_bpm',
    lowerLimit: 'pulserate_lower_bpm',
    display: true,
  },
  {
    type: 'TEMP',
    icon: ThermometerIcon,
    units: '°C',
    color: 'white',
    code: '8310-5',
    upperLimit: 'temperature_upper',
    lowerLimit: 'temperature_lower',
    display: true,
  },
  {
    type: 'NIBP',
    icon: BloodPressureIcon,
    units: 'mmHg',
    color: 'white',
    code: '85354-9',
    display: true,
  },
  {
    type: 'SYSTOLIC',
    icon: BloodPressureIcon,
    units: 'mmHg',
    color: 'white',
    code: '8480-6',
    upperLimit: 'bp_systolic_upper',
    lowerLimit: 'bp_systolic_lower',
    display: false,
  },
  {
    type: 'DIASTOLIC',
    icon: BloodPressureIcon,
    units: 'mmHg',
    color: 'white',
    code: '8462-4',
    upperLimit: 'bp_diastolic_upper',
    lowerLimit: 'bp_diastolic_lower',
    display: false,
  },
  {
    type: 'MEAN',
    icon: BloodPressureIcon,
    units: 'mmHg',
    color: 'white',
    code: '8478-0',
    upperLimit: 'bp_mean_upper',
    lowerLimit: 'bp_mean_lower',
    display: false,
  },
];

/* Exporting the PatientSectionDetails function. */
export default function PatientSectionMonitoringCharts(props) {
  /* Destructuring the props object and assigning the isEdit property to a variable of the same name. */
  const { patientID, pagePatient } = props;

  /* Importing the useDispatch hook from the react-redux library. */
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { searchResults, loading, error } = useSelector((state) => state.observation);

  const [initialized, setInitialized] = useState(false);
  const [type, setType] = useState('SPO2');
  const [date, setDate] = useState(moment().local().format('YYYY-MM-DD'));
  const [hour, setHour] = useState(moment().local().format('HH'));
  const [minute, setMinute] = useState(moment(Math.round(moment().local().minutes() / 10) * 10, 'mm').format('mm'));
  const [PatientConfig, setPatientConfig] = useState({
    toco_upper_contraction: 100,
    toco_lower_contraction: 0,
    heartrate_upper_bpm: 100,
    heartrate_lower_bpm: 50,
    respiratory_upper_rpm: 20,
    respiratory_lower_rpm: 12,
    pulserate_upper_bpm: 100,
    pulserate_lower_bpm: 50,
    bp_systolic_upper: 120,
    bp_systolic_lower: 90,
    bp_diastolic_upper: 80,
    bp_diastolic_lower: 60,
    temperature_upper: 42,
    temperature_lower: 35.8,
  });
  const [referenceMarkers, setReferenceMarkers] = useState();

  ObservationTypes[0].display = pagePatient.pregnant || false;
  ObservationTypes[1].display = pagePatient.pregnant || false;

  const setConfig = useCallback(() => {
    const config = {
      toco_upper_contraction: 100,
      toco_lower_contraction: 0,
      heartrate_upper_bpm: 100,
      heartrate_lower_bpm: 50,
      oxygen_upper_saturation: 100,
      oxygen_lower_saturation: 94,
      respiratory_upper_rpm: 20,
      respiratory_lower_rpm: 12,
      pulserate_upper_bpm: 100,
      pulserate_lower_bpm: 50,
      bp_systolic_upper: 120,
      bp_systolic_lower: 90,
      bp_diastolic_upper: 80,
      bp_diastolic_lower: 60,
      temperature_upper: 42,
      temperature_lower: 35.8,
    };

    Object.keys(config).forEach((key) => {
      if (pagePatient[`patient-lifeline-config-${key}`]) {
        if (
          pagePatient[`patient-lifeline-config-${key}`] !== '' &&
          !Number.isNaN(pagePatient[`patient-lifeline-config-${key}`])
        ) {
          config[key] = +pagePatient[`patient-lifeline-config-${key}`];
        }
      }
    });
    setPatientConfig(config);
  }, [pagePatient]);

  const setMarkers = useCallback(() => {
    const markers = [];

    if (type !== 'NIBP') {
      const upperLimit = PatientConfig[ObservationTypes.find((el) => el.type === type).upperLimit];
      const lowerLimit = PatientConfig[ObservationTypes.find((el) => el.type === type).lowerLimit];

      markers.push({
        axis: 'y',
        value: upperLimit,
        lineStyle: { stroke: 'red', strokeWidth: 1 },
        legend: 'Upper Limit',
        legendOrientation: 'horizontal',
      });

      markers.push({
        axis: 'y',
        value: lowerLimit,
        lineStyle: { stroke: 'red', strokeWidth: 1 },
        legend: 'Lower Limit',
        legendOrientation: 'horizontal',
      });
    } else {
      const upperLimit = PatientConfig[ObservationTypes.find((el) => el.type === 'SYSTOLIC').upperLimit];
      const lowerLimit = PatientConfig[ObservationTypes.find((el) => el.type === 'SYSTOLIC').lowerLimit];

      markers.push({
        axis: 'y',
        value: upperLimit,
        lineStyle: { stroke: 'red', strokeWidth: 1 },
        legend: 'Systolic Upper Limit',
        legendOrientation: 'horizontal',
      });

      markers.push({
        axis: 'y',
        value: lowerLimit,
        lineStyle: { stroke: 'red', strokeWidth: 1 },
        legend: 'Systolic Lower Limit',
        legendOrientation: 'horizontal',
      });

      const diastolicUpperLimit = PatientConfig[ObservationTypes.find((el) => el.type === 'DIASTOLIC').upperLimit];
      const diastolicLowerLimit = PatientConfig[ObservationTypes.find((el) => el.type === 'DIASTOLIC').lowerLimit];

      markers.push({
        axis: 'y',
        value: diastolicUpperLimit,
        lineStyle: { stroke: 'red', strokeWidth: 1 },
        legend: 'Diastolic Upper Limit',
        legendOrientation: 'horizontal',
      });

      markers.push({
        axis: 'y',
        value: diastolicLowerLimit,
        lineStyle: { stroke: 'red', strokeWidth: 1 },
        legend: 'Diastolic Lower Limit',
        legendOrientation: 'horizontal',
      });
    }
    // console.log(markers);
    setReferenceMarkers(markers);
  }, [PatientConfig, type]);

  const chartData = ObservationTypes.filter((el) => el.type !== 'NIBP').map((type) => {
    let formattedData = {};
    if (type.type !== 'SYSTOLIC' && type.type !== 'DIASTOLIC' && type.type !== 'MEAN') {
      formattedData = {
        id: type.type,
        data: searchResults
          .filter(
            (entry) =>
              entry.resource.subject.reference.split('/')[1] === patientID &&
              entry.resource.code.coding[0].code === type.code
          )
          .map((entry) => ({
            x: moment.utc(entry.resource.effectiveDateTime).local().toDate(),
            // .format('HH:mm:ss')
            y: entry.resource.valueQuantity.value,
          })),
      };
    } else if (type.type === 'SYSTOLIC' || type.type === 'DIASTOLIC' || type.type === 'MEAN') {
      formattedData = {
        id: type.type,
        data: searchResults
          .filter(
            (entry) =>
              entry.resource.subject.reference.split('/')[1] === patientID &&
              entry.resource.code.coding[0].code === ObservationTypes.find((el) => el.type === 'NIBP').code
          )
          .map((entry) => ({
            x: moment.utc(entry.resource.effectiveDateTime).local().toDate(),
            //
            y: entry.resource.component.filter((component) => component.code.coding[0].code === type.code)[0]
              .valueQuantity.value,
          })),
      };
    }

    return formattedData;
  });

  // console.log(chartData);

  // create a chartDisplay variable that contains the data from chartData that matches the code but if the code is for NIBP then it will return an array of 3 objects

  const chartDisplay = chartData.filter((data) =>
    type !== 'NIBP' ? data.id === type : data.id === 'SYSTOLIC' || data.id === 'DIASTOLIC' || data.id === 'MEAN'
  );
  // console.log(chartDisplay);
  const hasToDisplay = chartDisplay.some((obj) => obj.data.length > 0);
  // console.log(hasToDisplay);

  // const hasMarkers = chartDisplay.some((obj) => obj.data.length > 0);
  /**
   * Initializes the data for the page.
   */
  const initializeData = useCallback(() => {
    if (searchParams.get('type')) {
      setType(searchParams.get('type'));
    } else {
      searchParams.set('type', type);
    }

    if (searchParams.get('date')) {
      setDate(searchParams.get('date'));
    } else {
      searchParams.set('date', date);
    }

    if (searchParams.get('hour')) {
      setHour(searchParams.get('hour'));
    } else {
      searchParams.set('hour', hour);
    }

    if (searchParams.get('minute')) {
      setMinute(searchParams.get('minute'));
    } else {
      searchParams.set('minute', minute);
    }
    setSearchParams(searchParams);
    // console.log('initializeData');
    // console.log(date, hour, minute, searchParams, type);
    setInitialized(true);
  }, [date, hour, minute, searchParams, setSearchParams, type]);

  const getObservationData = useCallback(() => {
    const dateTime = moment(`${date} ${hour}:${minute}`, 'YYYY-MM-DD HH:mm');

    // round the start time down to the nearest minute using moment.js
    const start = dateTime.clone().utc();
    // round the end time up to the nearest minute using moment.js
    const end = dateTime.clone().utc().add(10, 'minute');

    const { code } = ObservationTypes.filter((el) => el.type === type)[0];

    dispatch(
      searchObservation({
        query: `subject=Patient/${patientID}&date=ge${start.toISOString()}&date=le${end.toISOString()}&code=${code}&_count=200&_sort=-date`,
      })
    );
  }, [date, dispatch, hour, minute, patientID, type]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /* Calling the getObservationData function when the component mounts. */
  useEffect(() => {
    if (initialized) {
      getObservationData();
    }
  }, [getObservationData, initialized]);

  useEffect(() => {
    setConfig();
  }, [setConfig]);

  useEffect(() => {
    setMarkers();
  }, [setMarkers]);

  return (
    <Grid container columnSpacing={2} rowSpacing={3}>
      <Grid item xs={12} md={4}>
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              fullWidth
              select
              label="Observation Type"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                searchParams.set('type', e.target.value);
                setSearchParams(searchParams);
              }}
            >
              {ObservationTypes.filter((el) => el.display).map((el) => (
                <MenuItem key={el.type} value={el.type}>
                  {el.type}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Grid>

      <Grid item xs={12} md={8}>
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <DatePicker
              label="Date"
              value={date ? moment(date, 'YYYY-MM-DD') : null}
              onChange={(value) => {
                if (value?.isValid) {
                  setDate(moment(value).format('YYYY-MM-DD'));
                  searchParams.set('date', moment(value).format('YYYY-MM-DD'));
                  setSearchParams(searchParams);
                } 
              }}
              slotProps={{ textField: { fullWidth: true }}}
            />

            <TextField
              fullWidth
              select
              label="Hour"
              value={hour}
              onChange={(e) => {
                setHour(e.target.value);
                searchParams.set('hour', e.target.value);
                setSearchParams(searchParams);
              }}
            >
              {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                <MenuItem key={hour} value={hour}>
                  {hour}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label="Minute"
              value={minute}
              onChange={(e) => {
                setMinute(e.target.value);
                searchParams.set('minute', e.target.value);
                setSearchParams(searchParams);
              }}
            >
              {Array.from({ length: 6 }, (_, i) => (i * 10).toString().padStart(2, '0')).map((min) => (
                <MenuItem key={min} value={min}>
                  {min} to {parseInt(min, 10) + 10}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Grid>
      {loading && (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      )}
      {error !== '' && !loading && (
        <Grid item xs={12}>
          {error}
        </Grid>
      )}

      {!loading && error === '' && !hasToDisplay && (
        <Grid item xs={12}>
          No data to display.
        </Grid>
      )}

      {!loading && error === '' && hasToDisplay && (
        <Grid item xs={12}>
          <div style={{ width: '100%', height: '600px' }}>
            <ResponsiveLine
              enableArea
              // data={observationData}
              data={chartDisplay}
              margin={{ top: 50, right: 20, bottom: 75, left: 50 }}
              // margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
              // xScale={{ type: 'point', min: `${hour}:${minute}`, max: `${hour}:${parseInt(minute, 10) + 10}` }}
              xScale={{
                type: 'time',
                format: '%Y-%m-%dT%H:%M:%S.%L%Z',
                precision: 'second',
              }}
              xFormat="time:%H:%M:%S"
              // {(value) => moment(value).format('HH:mm:ss')}
              // {...chartProps}
              yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickValues: 'every 1 minute',
                format: '%H:%M:%S',
                orient: 'bottom',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -45,
                legend: 'Time',
                legendOffset: 50,
                legendPosition: 'middle',
              }}
              // axisLeft={{
              //   orient: "left",
              //   tickSize: 5,
              //   tickPadding: 5,
              //   tickRotation: 0,
              //   legend: "count",
              //   legendOffset: -40,
              //   legendPosition: "middle",
              // }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: `${ObservationTypes.find((el) => el.type === type).units}`,
                legendOffset: -40,
                legendPosition: 'middle',
              }}
              colors={{ scheme: 'nivo' }}
              pointSize={10}
              // pointColor={{ theme: "background" }}
              pointColor={{ theme: 'labels.text.fill' }}
              pointBorderWidth={2}
              // pointBorderColor={{ from: "serieColor" }}
              pointBorderColor={{ from: 'serieColor', modifiers: [] }}
              pointLabel="y"
              pointLabelYOffset={-12}
              useMesh
              // curve="natural"
              enableSlices="x"
              // theme={{
              //   tooltip: {
              //     container: {
              //       background: "#333",
              //     },
              //   },
              // }}
              // tooltip={({ id, value, color }) => {
              //   console.log(color);
              //   console.log('test');
              //   return (<strong style={{ color }}>
              //     {id}: {value}
              //   </strong>)
              // }}
              sliceTooltip={({ slice }) => (
                <div
                  style={{
                    background: 'white',
                    padding: '9px 12px',
                    border: '1px solid #ccc',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <strong>{slice.points[0].data.x ? moment(slice.points[0].data.x).format('HH:mm:ss') : ''}</strong>
                  </div>
                  {slice.points.map((point) => (
                    <Grid
                      container
                      key={point.id}
                      style={{
                        // color: point.serieColor,
                        padding: '3px 0',
                      }}
                    >
                      <Grid item xs>
                        <span
                          style={{
                            display: 'initial',
                            color: point.serieColor,
                            paddingRight: '10px',
                            float: 'left',
                          }}
                        >
                          <strong>{point.serieId}</strong>
                          {':'}
                        </span>
                      </Grid>
                      <Grid item xs>
                        <span style={{ display: 'initial', float: 'right' }}>{point.data.yFormatted}</span>
                      </Grid>
                    </Grid>
                  ))}
                </div>
              )}
              curve="catmullRom"
              legends={[
                {
                  anchor: 'top',
                  direction: 'row',
                  justify: false,
                  translateX: -20,
                  translateY: -30,
                  itemsSpacing: 40,
                  itemDirection: 'left-to-right',
                  itemWidth: 80,
                  itemHeight: 20,
                  itemOpacity: 0.75,
                  symbolSize: 12,
                  symbolShape: 'circle',
                  symbolBorderColor: 'rgba(0, 0, 0, .5)',
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemBackground: 'rgba(0, 0, 0, .03)',
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
              markers={referenceMarkers}
            />
          </div>
        </Grid>
      )}
    </Grid>
  );
}
